import { useContext, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import BasicDatatable from "../Common/BasicDatatable";
import { columns, initialSort } from "./teamInsightsTableConfig";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { getUserAttributes } from "../../utils/auth";
import { GlobalContext } from "../../context/GlobalContext";
import AnalyticalAnalysisService from "../../services/analytics/AnalyticsService";
import { convertToUTC } from "../../utils/utils";
import SlideOver from "../Common/SlideOver";
import ClientRatingOverview from "../ClientOverview/ClientRatingOverview";
import { useLocation, useNavigate } from "react-router-dom";
import { DateRangeValue } from "../../utils/types";

const generateRows = (teamInsignts: any, newUserTeamData: any, selectedTab: any, tableFilters: any,
   ClientCountClick: (
    selectedNps: string,
    isSliderOverOpen: boolean,
    employeeEmail: string,
    employeePorId: string,
    userTeamData: any,
    employeeName?: string,
   ) => void
  ) =>
  teamInsignts?.map((item: any) => ({
    email: item.employeeEmail,
    employeeName: item.employeeName,
    num_meetings: item.numberofInteractions,
    total_client: item.totalClients,
    detractor_count: item.redRatedClients,
    rated_client_count: item.ratedClients,
    detractor_percent: item.detractor_percent,
    avgCallDuration: item.avgCallDuration,
    avgSelfTalkTime: item.avgSelfTalkTime,
    newUserTeamData: newUserTeamData,
    selectedTab: selectedTab,
    tableFilters: tableFilters,
    totalClients: item.totalClients,
    detractor_rated_count: item.detractorClientsCount,
    neutral_count: item.neutralClientsCount,
    promoter_count: item.promoterClientsCount,
    not_rated_count: item.notRatedClientsCount,
    profileOrgId: item.profileOrgRelationshipId,
    ClientCountClick: ClientCountClick,
  }));
  interface FilterValues {
    searchText: string;
    filterBy: string;
  }

const TeamInsights = ({
  selectedUser,
  selectedTab,
  newUserTeamData,
  analyticsDateRange,
}: {
  selectedUser: any;
  selectedTab: string;
  newUserTeamData: any
  analyticsDateRange: DateRangeValue;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showSnackbar } = useDisplaySnackbar();
  const loggedInEmail = getUserAttributes()?.email;
  const [teamInsights, setTeamInsights] = useState<any[]>([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [rows, setRows] = useState<any[]>([]);
  const [loadingTeamInsights, setLoadingTeamInsights] = useState(true);
  const { accountDetails } = useContext(GlobalContext);
  const [sort, setSort] = useState(initialSort);
  const [pageSize, setPageSize] = useState(50);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [filtersModified, _setFiltersModified] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState<"direct_reporties" | "account_managers">("direct_reporties");
  const defaultFilters: FilterValues[] = [
    { filterBy: "interactionType", searchText: "external" },
    { filterBy: "isRated", searchText: "rated" }
  ];
  const [selectedNps, setSelectedNps] = useState<string>("");
  const [isSlideOverOpen, setIsSlideOverOpen] = useState( false);
  const [ clickeduserEmail, setClickedUserEmail ] = useState<string>("");
  const parentPageIdentifier="analytics-page";
  const slideOverParent = "analytics-slideover";
  const customDateRange = useMemo(() => ({
    startDate: analyticsDateRange?.startDate || "",
    endDate: analyticsDateRange?.endDate || "",
  }), [analyticsDateRange?.startDate, analyticsDateRange?.endDate]);

  const incomingSlideOverParent = location.state?.slideOverParent;
  const initialSelectedNps = location.state?.selectedNps;
  const incomingClickeduserEmail = location.state?.clickeduserEmail;
    useEffect(() => {
      if (incomingSlideOverParent === slideOverParent) {
        setIsSlideOverOpen(true);
        setSelectedNps(initialSelectedNps);
        setClickedUserEmail(incomingClickeduserEmail);
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
      }
    }, [incomingSlideOverParent ]);

  const [tableFilter, _setTableFilter] = useState<FilterValues>({
        searchText: "",
        filterBy: "",
      });
      const [tableFilters, _setTableFilters] = useState<FilterValues[]>(defaultFilters);
      

  const apiFilters = tableFilters.reduce((acc, filter) => {
    if (filter.filterBy && filter.searchText) {
      acc[filter.filterBy] = filter.searchText;
    }
    return acc;
  }, {} as Record<string, string>);

  const finalApiFilters = filtersModified ? apiFilters : {
    interactionType: "external",
    isRated: "rated",
  }; // 🆕 Final API filters

  const loggedInProfileOrgId = accountDetails?.profileOrgId ?? "";
  
  const employeePorId = selectedTab === "my-analytics" ? loggedInProfileOrgId : newUserTeamData?.currentUser || loggedInProfileOrgId;
  const shouldCallApi = !!(employeePorId && (
    ( analyticsDateRange?.startDate && analyticsDateRange?.endDate) ||
    ( analyticsDateRange?.startDate  &&analyticsDateRange?.endDate &&
      !(tableFilter.filterBy === "" && tableFilter.searchText === ""))
  ));
  const {
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    data: teamAnalyticsData,
  } = AnalyticalAnalysisService.useGetUserAnalyticsNew(
    {
      startTime: convertToUTC(analyticsDateRange?.startDate),
      endTime: convertToUTC(analyticsDateRange?.endDate),
      employeePorId,
      isTeamAnalytics: selectedTab === "team-analytics" ? true : false,
      sortBy: sort.sort_field,
      sortOrder: sort.sort_order,
      offset: currentPageIndex,
      limit: pageSize,
      interactionType: finalApiFilters["interactionType"],
      isRated: finalApiFilters["isRated"],
      accountManagerView: selectedTab === "team-analytics" &&  activeSubTab === "account_managers" ? true : false,
    },
    shouldCallApi
  );

  useEffect(() => {
    setActiveSubTab("direct_reporties");
  }, [selectedTab]);

  useEffect(() => {
    setLoadingTeamInsights(isLoading || isFetching);
    setShowSpinner(isLoading || isFetching);
    if (isSuccess && teamAnalyticsData) {
      const teamAnalyticsList = teamAnalyticsData?.data?.data;
      const totalRecords = teamAnalyticsData?.totalRecords;
      if (teamAnalyticsList) {
        const transformedData = Object.keys(teamAnalyticsList).map((email) => ({
          email,
          ...teamAnalyticsList[email],
        }));
        setTeamInsights(transformedData || []);
        setTotalRecord(totalRecords);
      }

      setLoadingTeamInsights(false);
    }
    if (isError) {
      showSnackbar("error", error);
      setLoadingTeamInsights(false);
    }
  }, [isLoading, isFetching, isSuccess, isError, teamAnalyticsData]);

  const ClientCountClick = (
    selectedNps: string,
    isSliderOverOpen: boolean,
    _employeeEmail: string,
    rowEmployeePorId: string,
    _userTeamData: any,
    employeeName?: string,
  ) => {
    setSelectedNps(selectedNps);
    setIsSlideOverOpen(isSliderOverOpen);

    const isAccountManagerTab = selectedTab === "team-analytics" && activeSubTab === "account_managers";
    const selectedPorId = isAccountManagerTab ? employeePorId : rowEmployeePorId;

    navigate("/accounts/customer", {
      state: {
        rating: selectedNps,
        analyticsSelectedUserPorOrgId: selectedPorId,
        accountViewState: {
          selectedView: "org",
          selectedUserPorOrgId: selectedPorId,
        },
        customDateRange: {
          startDate: analyticsDateRange?.startDate,
          endDate: analyticsDateRange?.endDate,
        },
        status: ["active"],
        ...(isAccountManagerTab &&
          {
            accountManagerName: employeeName,
            showAccountManagerColumn: true,
          }
        )
      },
    });
  };

  useEffect(() => {
    if (teamInsights && teamInsights.length > 0) {
      let filteredInsights = teamInsights;

      const generated = generateRows(
        filteredInsights,
        newUserTeamData,
        selectedTab,
        tableFilters,
        ClientCountClick
      );

      setRows(generated);
      setTotalRecord(generated?.length || 0);
    } else {
      setRows(generateRows([], newUserTeamData, selectedTab, tableFilters, ClientCountClick));
    }
  }, [teamInsights, selectedTab, activeSubTab, selectedUser, loggedInEmail, newUserTeamData, tableFilters]);

  const handleSubTabChange = (newSubTab: "direct_reporties" | "account_managers") => {
    if (newSubTab !== activeSubTab) {
      setActiveSubTab(newSubTab);
    }
  };

  const onSortingChange = (sorting: any[]) => {
    if (sorting && sorting.length > 0) {
      setSort && setSort({
        sort_field: sorting[0].id,
        sort_order: sorting[0].desc === true ? "desc" : "asc",
      });
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const onPageChange = (value: number) => {
    setCurrentPageIndex(value);
  };
  const handlePageIndexChange = (newPageIndex: number) => {
    onPageChange(newPageIndex+1);
  };

  const customToolbarComponent = selectedTab === "team-analytics" ? (
    <Box sx={{ display: "flex", gap: "8px", alignItems: "center" }}>
      <button
        className={`account-manager-button ${activeSubTab === "direct_reporties" ? "active" : "inactive"}`}
        onClick={() => handleSubTabChange("direct_reporties")}
      >
        <span className="account-manager-button-text">Direct Reports</span>
      </button>
      <button
        className={`account-manager-button ${activeSubTab === "account_managers" ? "active" : "inactive"}`}
        onClick={() => handleSubTabChange("account_managers")}
      >
        <span className="account-manager-button-text">Account Managers</span>
      </button>
    </Box>
  ) : null;

  return (   
    <Box className="relative">
      {/* Spinner Overlay */}
      {loadingTeamInsights && (
      <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 z-10">
        <CircularProgress sx={{ color: "#ccc", opacity: 0.5  }} thickness={5} size={60} />
      </div>
      )} 
      <BasicDatatable
        columns={columns(analyticsDateRange?.startDate, analyticsDateRange?.endDate)}
        rows={rows}
        rowCount={totalRecord}
        initialSort={initialSort}
        onSortingChange={onSortingChange}
        onPageSizeChange={(newPageSize: number | null | undefined) => {
          if (newPageSize !== null && newPageSize !== undefined) {
            handlePageSizeChange(newPageSize);
          }
        }}
        onPaginationChange={handlePageIndexChange}
        tabLabel={selectedTab === "my-analytics" ? "My Insights" : "Team Insights"}
        customToolbarComponent={customToolbarComponent}
        columnVisibility={{client_list: false, num_meetings: false,
          avgCallDuration: false, avgSelfTalkTime: false,
        }}
        minHeight="calc(91vh - 310px)"
        maxHeight="calc(80vh - 300px)"
        showSpinner={showSpinner} 
      />
      <SlideOver isOpen={isSlideOverOpen} onClose={() => setIsSlideOverOpen(false)}>
        {isSlideOverOpen && (
          <ClientRatingOverview isSlideOverOpen = {isSlideOverOpen}
          selectedNps={selectedNps}  slideOverParent={slideOverParent}
          parentPageIdentifier={parentPageIdentifier}
          viewMode="count"
          customDateRange={customDateRange}
          selectedTab ={selectedTab}
          clickeduserEmail= {clickeduserEmail}
          />
        )}
      </SlideOver>
    </Box>
  );
};

export default TeamInsights;
